import React from "react";
import { Link, useLocation } from "react-router-dom";
import { styled, keyframes } from "@mui/material/styles";
import { Box, Typography, Button, Container } from "@mui/material";
import {
  ErrorOutline as ErrorIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { ROUTES } from "../../routes/User/paths";

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled(Box)({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)",
  position: "relative",
  overflow: "hidden",
});

const StarsBackground = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `
    radial-gradient(2px 2px at 20px 30px, #fff, transparent),
    radial-gradient(2px 2px at 40px 70px, rgba(255,255,255,0.8), transparent),
    radial-gradient(1px 1px at 90px 40px, #fff, transparent),
    radial-gradient(2px 2px at 160px 120px, rgba(255,255,255,0.9), transparent),
    radial-gradient(1px 1px at 230px 80px, #fff, transparent),
    radial-gradient(2px 2px at 300px 150px, rgba(255,255,255,0.7), transparent),
    radial-gradient(1px 1px at 350px 200px, #fff, transparent),
    radial-gradient(2px 2px at 420px 50px, rgba(255,255,255,0.8), transparent),
    radial-gradient(1px 1px at 500px 180px, #fff, transparent),
    radial-gradient(2px 2px at 580px 90px, rgba(255,255,255,0.9), transparent)
  `,
  backgroundRepeat: "repeat",
  backgroundSize: "600px 250px",
  animation: `${pulse} 4s ease-in-out infinite`,
  pointerEvents: "none",
});

const FloatingOrb = styled(Box)({
  position: "absolute",
  borderRadius: "50%",
  filter: "blur(60px)",
  animation: `${float} 6s ease-in-out infinite`,
  pointerEvents: "none",
});

const ContentWrapper = styled(Container)({
  position: "relative",
  zIndex: 10,
  textAlign: "center",
  animation: `${fadeIn} 0.8s ease-out`,
});

const ErrorCode = styled(Typography)({
  fontSize: "8rem",
  fontWeight: 900,
  lineHeight: 1,
  background: "linear-gradient(180deg, #ff6b6b 0%, #ee5a5a 50%, #c92a2a 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textShadow: "none",
  filter: "drop-shadow(0 4px 8px rgba(255, 107, 107, 0.3))",
  marginBottom: "1rem",
});

const ErrorTitle = styled(Typography)({
  fontSize: "2rem",
  fontWeight: 700,
  color: "#fff",
  marginBottom: "1rem",
  letterSpacing: "-0.02em",
});

const ErrorMessage = styled(Typography)({
  fontSize: "1.1rem",
  color: "rgba(255, 255, 255, 0.7)",
  marginBottom: "2.5rem",
  maxWidth: "400px",
  margin: "0 auto 2.5rem",
  lineHeight: 1.6,
});

const ButtonGroup = styled(Box)({
  display: "flex",
  gap: "1rem",
  justifyContent: "center",
  flexWrap: "wrap",
});

const PrimaryButton = styled(Button)({
  background: "linear-gradient(135deg, #d44402 0%, #ff6b35 100%)",
  color: "#fff",
  padding: "12px 32px",
  borderRadius: "50px",
  fontWeight: 600,
  fontSize: "1rem",
  textTransform: "none",
  boxShadow: "0 4px 20px rgba(212, 68, 2, 0.4)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #ff6b35 0%, #d44402 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 25px rgba(212, 68, 2, 0.5)",
  },
});

const SecondaryButton = styled(Button)({
  background: "transparent",
  color: "#fff",
  padding: "12px 32px",
  borderRadius: "50px",
  fontWeight: 600,
  fontSize: "1rem",
  textTransform: "none",
  border: "2px solid rgba(255, 255, 255, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "#fff",
    background: "rgba(255, 255, 255, 0.1)",
  },
});

const PathDisplay = styled(Box)({
  background: "rgba(255, 255, 255, 0.05)",
  borderRadius: "8px",
  padding: "12px 24px",
  marginTop: "2rem",
  display: "inline-block",
});

const PathText = styled(Typography)({
  fontFamily: "monospace",
  fontSize: "0.9rem",
  color: "rgba(255, 255, 255, 0.6)",
});

const NotFound = () => {
  const location = useLocation();
  const path = location.pathname;

  return (
    <PageContainer>
      <StarsBackground />
      
      <FloatingOrb
        sx={{
          width: "400px",
          height: "400px",
          background: "rgba(212, 68, 2, 0.3)",
          top: "-100px",
          right: "-100px",
          animationDelay: "0s",
        }}
      />
      <FloatingOrb
        sx={{
          width: "300px",
          height: "300px",
          background: "rgba(138, 43, 226, 0.2)",
          bottom: "-50px",
          left: "-50px",
          animationDelay: "2s",
        }}
      />

      <ContentWrapper maxWidth="md">
        <ErrorIcon
          sx={{
            fontSize: "5rem",
            color: "#d44402",
            mb: 3,
            animation: `${float} 3s ease-in-out infinite`,
          }}
        />

        <ErrorCode>404</ErrorCode>

        <ErrorTitle>Page Not Found</ErrorTitle>

        <ErrorMessage>
          Oops! The page you're looking for doesn't exist. 
          It might have been moved, deleted, or you may have mistyped the URL.
        </ErrorMessage>

        <ButtonGroup>
          <PrimaryButton
            component={Link}
            to={ROUTES.dashboard}
            startIcon={<HomeIcon />}
          >
            Go to Dashboard
          </PrimaryButton>
          
          <SecondaryButton
            onClick={() => window.history.back()}
            startIcon={<ArrowBackIcon />}
          >
            Go Back
          </SecondaryButton>
        </ButtonGroup>

        <PathDisplay>
          <SearchIcon sx={{ fontSize: "1rem", mr: 1, verticalAlign: "middle", color: "rgba(255,255,255,0.4)" }} />
          <PathText component="span">
            Requested path: <strong style={{ color: "#d44402" }}>{path}</strong>
          </PathText>
        </PathDisplay>
      </ContentWrapper>
    </PageContainer>
  );
};

export default NotFound;
